/** @jsx jsx */
import React from "react";
import { css, jsx } from "@emotion/core";
import { RecorderButton } from "components/atoms/Button";
import FlexBox from "components/atoms/FlexBox";
import FlexBoxItem from "components/atoms/FlexBoxItem";
import { grayScale } from "components/styles";
import BodyText from "components/atoms/BodyText";
import useGPSRecorder from "./hooks";
const isIpad = window.matchMedia("(max-width: 1024px)").matches;

const styles = {
  formRecorder: css({
    textAlign: "center"
  }),
  dateForm: css({
    width: "312px",
    margin: "auto",
    justifyContent: `${isIpad ? 'center' : ''}`
  }),
  formContainer: css({
    marginTop: "16px",
    borderRadius: "3px"
  }),
  imageButton: css({
    width: "100%",
    textAlign: 'center',
    height: '80px',
  }),
  ipadTimeContainer: css({
    display: "flex",
    flexWrap: "wrap",
    alignSelf: "center",
    width: "100%",
    marginLeft: '20px',
    justifyContent: 'center'
  })
};

const timeCss = css`
	align-items: baseline;
	justify-content: center;
	margin: auto;
	width: ${isIpad ? 'auto' : '350px'};
	color: red;
	line-height: ${isIpad ? '80px' : '100px'};
	span:first-of-type {
		font-size: ${isIpad ? '64px' : '100px'};
		color: ${grayScale.gray100};
		font-weight: bold;
	}
	span:last-of-type {
		font-size: 32px;
		color: ${grayScale.gray100};
	}
`;

const btnContainer = css`
	display: ${isIpad ? 'block' : 'flex'};
	text-align: center !important;
	flex-wrap: wrap;
	width: ${isIpad ? '100%' : '700px'};
	justify-content: center;
	margin: 30px auto 0 auto;
	height: auto;
	button {
		border-radius: 3px;
		border: 0;
		height: 120px;
		font-size: 24px;
		height: 120px;
		margin-bottom: 20px;
		margin-left: ${isIpad ? '0' : '30px'};
	}
	button:hover {
		opacity: 0.9;
	}
`;

const GPSRecorder: React.FC<{
  dataTimes: any;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  fetchHistoryData: any;
}> = ({ dataTimes, setIsLoading, fetchHistoryData }) => {
  const { stampStaff, handleStampEvent } = useGPSRecorder(setIsLoading, fetchHistoryData);
  const companyCode = sessionStorage.getItem('loginUser.companyCode') || '';
  
  return (
    <React.Fragment>
      <div css={styles.formContainer}>
          <div css={isIpad ? styles.ipadTimeContainer : {}}>
          {dataTimes.currentDay ? (
            <React.Fragment>
              <FlexBox customStyle={styles.dateForm}>
                <BodyText size="lg" color={grayScale.gray100}>
                  {dataTimes.currentDay}
                </BodyText>
              </FlexBox>
              <FlexBox customStyle={timeCss}>
                <BodyText>{dataTimes.timeNow}</BodyText>
                <BodyText>{dataTimes.second}</BodyText>
              </FlexBox>
            </React.Fragment>
          ) : (
            <div
              style={{
                fontSize: isIpad ? '30px' : '80px',
                textAlign: 'center',
                marginBottom: '20px'
              }}
            >
              サーバ時間取得中・・・
            </div>
          )}
        </div>
        <div style={{ width: "100%", textAlign: "center" }}>
							<BodyText color={grayScale.gray100} size={`${isIpad}` ? "md" : "xl"} bold={true}>
								{stampStaff?.staffName}
								{(stampStaff?.lastStampType === -1 || stampStaff?.lastStampType === 3) && "さん、出勤打刻を押してください。"}
								{(stampStaff?.lastStampType === 0 || stampStaff?.lastStampType === 2) && "さん、退勤打刻または休憩開始を押してください。"}
								{stampStaff?.lastStampType === 1 && "さん、休憩終了を押してください。"}
							</BodyText>
						</div>
        <FlexBox customStyle={btnContainer}>
          {/* in */}
          {/* Default UI */}
          {
            (stampStaff?.lastStampType === -1 ||
              stampStaff?.lastStampType === 3) && !stampStaff?.useRemote && (
              <FlexBoxItem basis="35%">
                <RecorderButton
                  text="出勤"
                  onClick={() => handleStampEvent("in", "")}
                  icon="checkInOffice"
                  background="#007BC3"
                />
              </FlexBoxItem>
            )
          }

          {/* New UI */}
          {
            (stampStaff?.lastStampType === -1 || stampStaff?.lastStampType === 3) && 
             stampStaff?.useRemote && (
              <React.Fragment>
                <FlexBoxItem basis="48%">
                  <RecorderButton
                    text="オフィス出勤"
                    onClick={() => handleStampEvent("in", "", false)}
                    icon="checkInOffice"
                    background="#007BC3"
                  />
                </FlexBoxItem>
                <FlexBoxItem basis="48%">
                  <RecorderButton
                    text={companyCode === 'HPo6wkyuze' ? '在宅勤務' : 'テレワーク出勤'}
                    textMin={companyCode === 'HPo6wkyuze' ? '(自宅待機含む)': ''}
                    onClick={() => handleStampEvent("in", "", true)}
                    icon="checkInRemote"
                    background="#F8BF2D"
                  />
                </FlexBoxItem>
              </React.Fragment>
            )
          }

          {/* out */}
          {
            stampStaff?.lastStampType === 0 || stampStaff?.lastStampType === 2 ? (
              <FlexBoxItem basis="48%">
                <RecorderButton
                  text="退勤"
                  onClick={() => handleStampEvent("out", "")}
                  icon="checkOut"
                  background="#666666"
                />
              </FlexBoxItem>
            ) : null
          }

          {
            stampStaff?.useRecess &&
            (stampStaff?.lastStampType === 0 ||
             stampStaff?.lastStampType === 2) ? (
              <FlexBoxItem basis="48%">
                <RecorderButton
                  text="休憩開始"
                  onClick={() => handleStampEvent("recess_in", "")}
                  icon="breakStart"
                  background="#00B172"
                />
              </FlexBoxItem>
            ) : null
          }

          {
            stampStaff?.useRecess && stampStaff?.lastStampType === 1 ? (
              <FlexBoxItem basis="48%">
                <RecorderButton
                  text="休憩終了"
                  onClick={() => handleStampEvent("recess_out", "")}
                  icon="breakEnd"
                  background="#8B2E9C"
                />
              </FlexBoxItem>
            ) : null
          }

          {
            stampStaff?.lastStampType === 0 ||
            stampStaff?.lastStampType === 1 ||
            stampStaff?.lastStampType === 2 ? (
              <FlexBoxItem basis="48%">
                <RecorderButton
                  text="現在地登録"
                  onClick={() => handleStampEvent("current", "")}
                  icon="registerLocation"
                  background="#FB7971"
                />
              </FlexBoxItem>
            ) : null
          }
        </FlexBox>
      </div>
    </React.Fragment>
  );
};

export default GPSRecorder;
